import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Droplet, Eye, EyeOff, Key, Moon, Sun, UserRound } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import useAdminTheme from '../hooks/useAdminTheme';

export default function LoginPage() {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isDark, toggleTheme } = useAdminTheme();

  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.adminLogin(account, password);
      const payload = response.data?.data || response.data;
      if (!payload?.token || !payload?.user || payload.user.accountType !== 'admin') {
        throw new Error(response.data?.message || 'Admin authentication payload is missing');
      }
      setAuth(payload.token, payload.refreshToken || null, payload.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed. Please check the account and password, then try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-shell relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(47,128,237,0.12),transparent_32%)]" />
      <button
        type="button"
        onClick={toggleTheme}
        className="admin-secondary-button absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-xl transition"
        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
        title={isDark ? 'Light theme' : 'Dark theme'}
      >
        {isDark ? <Sun size={19} /> : <Moon size={19} />}
      </button>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-400 text-slate-950 shadow-2xl shadow-cyan-400/20">
            <Droplet size={36} fill="currentColor" />
          </div>
          <h1 className="admin-text-primary text-4xl font-black tracking-tight">AirKOP Admin</h1>
          <p className="admin-text-secondary mt-2 text-base">Backend operations account login</p>
        </div>

        <form onSubmit={handleLogin} className="admin-card rounded-2xl p-7 backdrop-blur-xl">
          {error && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-600 dark:text-rose-200">
              <AlertCircle className="mt-0.5 flex-shrink-0" size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="admin-text-secondary ml-1 text-[13px] font-bold uppercase">Admin Account</label>
              <div className="relative">
                <UserRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  required
                  className="admin-input w-full rounded-xl p-4 pl-12 text-base outline-none transition"
                  placeholder="Enter admin account"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="admin-text-secondary ml-1 text-[13px] font-bold uppercase">Password</label>
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  className="admin-input w-full rounded-xl p-4 pl-12 pr-12 text-base outline-none transition"
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-cyan-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="admin-primary-button w-full rounded-xl py-4 text-base font-black shadow-lg shadow-cyan-400/20 transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
